import { LoadingButton } from '@mui/lab';
import { Box, Card, CardContent, CircularProgress, Container, Divider, Grid, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import COMPRA_SEGURA from '~/assets/images/compra-segura.png';
import Header from '~/components/Header';
import CardPrice from '~/components/PersonalCoverage/CardPrice';
import JourneyQuoteInsuredHeader from '~/components/PersonalCoverage/JourneyQuote2026/JourneyQuoteInsuredHeader';
import JourneyQuoteProductAccordion from '~/components/PersonalCoverage/JourneyQuote2026/JourneyQuoteProductAccordion';
import JourneyQuoteSummaryColumn from '~/components/PersonalCoverage/JourneyQuote2026/JourneyQuoteSummaryColumn';
import QuotePdfDocument from '~/components/PersonalCoverage/JourneyQuote2026/QuotePdfDocument';
import { buildQuoteSubscriptionRows } from '~/components/PersonalCoverage/JourneyQuote2026/buildQuoteSubscriptionRows';
import { buildQuotePdfModel } from '~/components/PersonalCoverage/JourneyQuote2026/quotePdfModel';
import SidebarMesages from '~/components/PersonalCoverage/SidebarMesages';
import {
  JourneyLoadingScreen,
  StandardJourneyBrandedScreen,
  StandardJourneyCardHeader
} from '~/components/StandardJourney';
import { useJourneyAttribution } from '~/components/StandardJourney/useJourneyAttribution';
import { captureEvent } from '~/analytics/posthog';
import { useQuoteData } from '~/hooks/useQuoteData';
import ModalCoverage from '~/modals/ModalCoverage';
import ModalHelpClub from '~/modals/ModalHelpClub';
import ModalHelpFinancialCharges from '~/modals/ModalHelpFinancialCharges';
import ModalTermOfContract from '~/modals/ModalTermOfContract';
import ModalTermOfGarantee from '~/modals/ModalTermOfGarantee';
import useStore, { storeState } from '~/store';
import { Product, Quote } from '~/store/slices/quote/types';
import { reportError } from '~/utils/reportError';
import searchNameEquipment from '~/utils/searchNameEquipment';
import { buildPaymentQuoteUrl } from './newJourney/buildPaymentQuoteUrl';
import { usePersonalCoverageProducts, usePersonalCoverageRedirect, usePersonalCoverageSummary } from './hooks';
import { resolveClubDisplayAmount, shouldShowFinancialCharges } from './utils/planType.utils';
import * as styles from './styles';

export type QuotePersonalizationProps = {
  variant: 'journey' | 'legacy';
  storySkipQuoteFetch?: boolean;
};

const priceFormatter = (value: number) =>
  new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, style: 'currency', currency: 'BRL' }).format(value);

export default function QuotePersonalization({ variant, storySkipQuoteFetch = false }: QuotePersonalizationProps) {
  const isJourney = variant === 'journey';
  const [contractLoading, setContractLoading] = useState(false);
  const [checkedTermOfContract, setCheckedTermOfContract] = useState(false);
  const [checkedTermOfGarantee, setCheckedTermOfGarantee] = useState(false);
  const [checkedTermOfTracking, setCheckedTermOfTracking] = useState(false);

  const [searchParams] = useSearchParams();
  const planParam = searchParams.get('plan');
  const distribuitorParam = searchParams.get('dis');
  const orgParam = searchParams.get('org');
  const sellerParam = searchParams.get('seller');

  const navigate = useNavigate();
  const { clubName: attributionClubName, sellerName } = useJourneyAttribution();

  const homePath = useMemo(() => {
    const queryTail = `${distribuitorParam ? `&dis=${distribuitorParam}` : ''}${orgParam ? `&org=${orgParam}` : ''}${
      sellerParam ? `&seller=${sellerParam}` : ''
    }`;
    return `/?plan=${planParam ?? ''}${queryTail}`;
  }, [distribuitorParam, orgParam, planParam, sellerParam]);

  const { quoteLoading } = useQuoteData({
    redirectToHome: true,
    quoteParamName: 'quote',
    skipFetch: storySkipQuoteFetch
  });
  const quotes = useStore((s) => s.quotes);

  useEffect(() => {
    if (searchParams.get('quote')) return;
    useStore.getState().clearPersistedQuoteDraft();
  }, [searchParams]);
  const { products, productsLoading, handleProduct } = usePersonalCoverageProducts(orgParam);
  usePersonalCoverageRedirect(quoteLoading, quotes);
  const { isOnlyProduct9, isGuaranteeSelected, isTrackingSelected, isProductFree, shouldDisableSubmit } =
    usePersonalCoverageSummary(checkedTermOfContract, checkedTermOfGarantee, checkedTermOfTracking);

  const quoteResponse = useStore((s) => s.quoteResponse);
  const quoteAsyncUpdate = useStore((s) => s.quoteAsyncUpdate);
  const equipment = useStore((s) => s.quote.equipment);
  const equipmentValue = useStore((s) => s.quote.equipmentValue);
  const typeClub = useStore((s) => s.quoteResponse?.type_club);
  const clubName = useStore((s) => s.quoteResponse?.plan_club_name);
  const membershipFee = useStore((s) => s.quoteResponse?.membership_fee) ?? 0;
  const totalValue = useStore((s) => s.totalValue);
  const serviceCharge = useStore((s) => s.serviceCharge);
  const promotionalMonths = useStore((s) => s.quoteResponse?.promotional_months);
  const has_promotion = useStore((s) => s.promotionData?.has_promotion);
  const { flat_rate, plan_club_name, plan_has_club, plan_club_description, plan_type } = quoteResponse || {};
  const clubDisplayAmount = resolveClubDisplayAmount(flat_rate ?? 0, totalValue, plan_type);
  const showFinancialCharges = shouldShowFinancialCharges(plan_type, isOnlyProduct9);
  const insured = useStore((s) => s.insured);
  const quoteFormName = useStore((s) => s.quote.name);

  const displayInsuredName = useMemo(() => {
    const n = (insured.social_name || insured.name || quoteFormName || '').trim();
    return n || null;
  }, [insured.social_name, insured.name, quoteFormName]);

  const quoteToProduct = useCallback((q: Quote): Product => {
    return {
      product_description: q.product_description,
      product_id: q.product_id,
      product_name: q.product_name,
      product_price: q.product_price
    };
  }, []);

  const journeySubscriptionRows = useMemo(
    () =>
      buildQuoteSubscriptionRows({
        flat_rate,
        plan_club_description,
        plan_club_name,
        plan_has_club,
        products
      }),
    [flat_rate, plan_club_description, plan_club_name, plan_has_club, products]
  );

  const journeyMonthlyTotal = isOnlyProduct9 ? 0 : totalValue;

  // PDF da cotação (Figma 1700:48 / 1700:132). O link do QR Code é a própria URL
  // atual, que já carrega `?quote=` e portanto retoma a cotação.
  const quotePdfModel = useMemo(
    () =>
      buildQuotePdfModel({
        clubName: plan_club_name || attributionClubName,
        customerDocument: insured.document || '',
        customerName: displayInsuredName ?? '',
        equipmentLabel: equipment ? searchNameEquipment(equipment) : '',
        equipmentValue,
        installments: quoteResponse?.installments ?? 0,
        membershipFee,
        monthlyTotal: journeyMonthlyTotal,
        operationalCost: serviceCharge,
        quoteResponse,
        resumeUrl: typeof window === 'undefined' ? '' : window.location.href,
        selectedQuotes: quotes.filter((q) => q.checked),
        sellerName
      }),
    [
      attributionClubName,
      displayInsuredName,
      equipment,
      equipmentValue,
      insured.document,
      journeyMonthlyTotal,
      membershipFee,
      plan_club_name,
      quoteResponse,
      quotes,
      sellerName,
      serviceCharge
    ]
  );

  const handleDownloadPdf = useCallback(() => {
    captureEvent('quote_pdf_download_clicked', { quote_id: quoteResponse?.quote_id });
    window.print();
  }, [quoteResponse?.quote_id]);

  const handleContract = useCallback(async () => {
    if (!quoteResponse?.quote_hash) return;

    setContractLoading(true);
    try {
      await quoteAsyncUpdate(quoteResponse.quote_hash, products, orgParam ?? undefined);
      captureEvent('coverage_advanced_to_payment', {
        quote_hash: quoteResponse.quote_hash,
        products: products.length
      });
      setContractLoading(false);
      const currentQuoteHash = searchParams.get('quote');
      if (!currentQuoteHash) return;
      navigate(
        buildPaymentQuoteUrl({
          quoteHash: currentQuoteHash,
          plan: planParam,
          distribuitor: distribuitorParam,
          org: orgParam,
          seller: sellerParam,
          channel: searchParams.get('channel')
        })
      );
    } catch (error: unknown) {
      setContractLoading(false);
      reportError('Erro ao finalizar contratação', error as Error, {
        context: 'PersonalCoverage_handleContract',
        quoteHash: quoteResponse?.quote_hash,
        productsCount: products?.length
      });
      let message = 'Não foi possível realizar a sua assinatura no momento. Por favor, tente novamente mais tarde.';
      if (error && typeof error === 'object' && 'isAxiosError' in error) {
        const notifications = (error as { response?: { data?: { notifications?: Array<{ message?: string }> } } })
          .response?.data?.notifications;
        if (notifications?.[0]?.message) message = notifications[0].message;
      }
      storeState.addToast({ title: 'Error', message, type: 'error' });
    }
  }, [
    quoteResponse?.quote_hash,
    quoteAsyncUpdate,
    products,
    orgParam,
    searchParams,
    distribuitorParam,
    sellerParam,
    planParam,
    navigate
  ]);

  // Termos: na jornada 2026 vivem na coluna de resumo (Figma 1680:5063);
  // no legado continuam abaixo dos produtos.
  const termsBlock = (
    <Box sx={{ mt: isJourney ? 0 : 2 }}>
      {/* Termo de uso: sempre presente, independente dos produtos escolhidos. */}
      <ModalTermOfContract
        checkedTermOfContract={checkedTermOfContract}
        organizationId={orgParam}
        setCheckedTermOfContract={setCheckedTermOfContract}
      />
      {isGuaranteeSelected ? (
        <ModalTermOfGarantee
          checkedTermOfGarantee={checkedTermOfGarantee}
          organizationId={orgParam}
          setCheckedTermOfGarantee={setCheckedTermOfGarantee}
        />
      ) : null}
      {isTrackingSelected ? (
        <ModalTermOfContract
          checkedTermOfContract={checkedTermOfTracking}
          organizationId={orgParam}
          setCheckedTermOfContract={setCheckedTermOfTracking}
          termType="TrackingService"
          linkLabel="termo de rastreamento"
        />
      ) : null}
    </Box>
  );

  const mainColumnInner = (
    <>
      {!isJourney ? (
        <Header
          goBackTo={homePath}
          subTitle="Escolha abaixo o que você precisa:"
          title="Vamos personalizar a sua cobertura?"
        />
      ) : null}
      {!isJourney ? (
        <Box sx={{ mb: 2 }}>
          {typeClub === 2 ? (
            <Typography component="h3" sx={styles.description} variant="h5">
              {clubName}
            </Typography>
          ) : (
            <Typography component="h3" sx={styles.description} variant="h5">
              {searchNameEquipment(equipment)} de{' '}
              {equipmentValue.toLocaleString('pt-BR', { currency: 'BRL', style: 'currency' })}
            </Typography>
          )}
          <Typography sx={styles.subTitle} variant="h6">
            <ModalCoverage />
            Aqui explicamos o que significa cada cobertura!
          </Typography>
        </Box>
      ) : null}
      <Box>
        {quotes.map((quoteItem: Quote) => (
          <Box key={quoteItem.product_id}>
            {isJourney ? (
              <JourneyQuoteProductAccordion
                clubType={typeClub}
                isFree={isProductFree(quoteItem.product_id)}
                isSelected={products.some((p) => p.product_id === quoteItem.product_id)}
                quote={quoteItem}
                showIncludedBadge={typeClub === 2 && quoteItem.required}
                onToggle={() => handleProduct(quoteToProduct(quoteItem))}
              />
            ) : (
              <CardPrice
                isFree={isProductFree(quoteItem.product_id)}
                isSelected={products.some((p) => p.product_id === quoteItem.product_id)}
                selectProduct={handleProduct}
                {...quoteItem}
              />
            )}
          </Box>
        ))}
      </Box>

      {products.length > 0 && has_promotion ? (
        <>
          <Divider sx={styles.dividerStyle} />
          <Box sx={{ py: 2, textAlign: 'center' }}>
            <Typography sx={{ fontWeight: 'bold' }} variant="h6">
              Valor promocional
            </Typography>
            <Typography sx={{ fontWeight: 'bold', mt: 1 }} variant="h4">
              {promotionalMonths && promotionalMonths > 0
                ? `${promotionalMonths} Meses GRÁTIS`
                : 'Gratuito até 31 de dezembro'}
            </Typography>
          </Box>
          <Typography
            sx={{
              borderTop: '1px solid #323232',
              color: '#A547FF',
              marginInline: 'auto',
              mb: 2,
              paddingTop: '1rem',
              textAlign: 'center',
              width: '60%'
            }}
            variant="h6"
          >
            {promotionalMonths && promotionalMonths > 0
              ? 'A partir da data de contratação.'
              : 'Válido para todos os usuários.'}
          </Typography>
        </>
      ) : null}

      {!isJourney ? termsBlock : null}

      {!isJourney && products.length > 0 && !has_promotion ? (
        <>
          <Divider sx={styles.dividerStyle} />
          <Typography component="h3" sx={styles.description} variant="h5">
            Resumo da assinatura
          </Typography>
          <Box sx={styles.containerAmount}>
            {typeClub === 2 ? (
              <Box sx={styles.containerResume}>
                <Box sx={styles.containerServiceCharge}>
                  <Box component="section" sx={styles.FinancialCharges}>
                    <Typography variant="body1">Taxa de adesão</Typography>
                  </Box>
                  <Typography variant="body1">
                    {productsLoading ? <CircularProgress size={20} /> : `1x ${priceFormatter(membershipFee)}`}
                  </Typography>
                </Box>
                <Divider sx={styles.amountDivider} />
                <Box sx={styles.containerTotal}>
                  <Typography sx={styles.totalStyle} variant="body1">
                    Plano anual {clubName}
                  </Typography>
                  <Typography sx={styles.amountStyle} variant="h6">
                    {productsLoading ? <CircularProgress size={20} /> : `12x ${priceFormatter(totalValue)}`}
                  </Typography>
                </Box>
                <Typography variant="body2">(Valor a ser pago mensalmente no cartão de crédito)</Typography>
              </Box>
            ) : (
              <Box>
                {plan_has_club ? (
                  <Box sx={styles.containerServiceCharge}>
                    <Box component="section" sx={styles.FinancialCharges}>
                      <Typography variant="body1">{plan_club_name}</Typography>
                      <ModalHelpClub clubDiscription={plan_club_description} />
                    </Box>
                    <Typography variant="body1">
                      {productsLoading ? <CircularProgress size={16} /> : priceFormatter(clubDisplayAmount)}
                    </Typography>
                  </Box>
                ) : null}

                {showFinancialCharges ? (
                  <Box sx={styles.containerServiceCharge}>
                    <Box component="section" sx={styles.FinancialCharges}>
                      <Typography variant="body1">Encargos financeiros</Typography>
                      <ModalHelpFinancialCharges />
                    </Box>
                    <Typography variant="body1">
                      {productsLoading ? <CircularProgress size={20} /> : priceFormatter(serviceCharge)}
                    </Typography>
                  </Box>
                ) : null}
                {showFinancialCharges ? <Divider sx={styles.amountDivider} /> : null}
                <Box sx={styles.containerTotal}>
                  <Typography sx={styles.totalStyle} variant="body1">
                    Valor Total ( x 12)
                  </Typography>
                  <Typography sx={styles.amountStyle} variant="h6">
                    {productsLoading ? <CircularProgress size={20} /> : priceFormatter(isOnlyProduct9 ? 0 : totalValue)}
                  </Typography>
                </Box>
                <Typography variant="body2">(Valor a ser pago mensalmente no cartão de crédito)</Typography>
                {membershipFee > 0 && !isOnlyProduct9 ? (
                  <>
                    <Divider sx={styles.amountDivider} />
                    <Box sx={styles.containerTotal}>
                      <Typography sx={styles.totalStyle} variant="body1">
                        Taxa de adesão ( x 1)
                      </Typography>
                      <Typography sx={styles.amountStyle} variant="h6">
                        {productsLoading ? <CircularProgress size={20} /> : priceFormatter(membershipFee)}
                      </Typography>
                    </Box>
                  </>
                ) : null}
              </Box>
            )}
          </Box>
        </>
      ) : null}

      {!isJourney ? (
        <Box sx={styles.containerButton}>
          <img alt="Compra segura" src={COMPRA_SEGURA} />
          <LoadingButton
            disabled={shouldDisableSubmit}
            loading={contractLoading}
            onClick={handleContract}
            size="large"
            sx={styles.butttonStyle}
            variant={products.length === 0 ? 'outlined' : 'contained'}
          >
            {isOnlyProduct9 ? 'Ativar garantia' : 'Quero contratar'}
          </LoadingButton>
        </Box>
      ) : null}
    </>
  );

  if (quoteLoading) {
    if (isJourney) {
      return (
        <StandardJourneyBrandedScreen
          appId="journey-quote-personalization-loading"
          contentLayout="fullscreen"
          onBack={() => navigate(homePath)}
          progressPercent={100}
          showBack
          showHeaderProgressBar={false}
        >
          <Box
            aria-busy="true"
            aria-live="polite"
            role="status"
            sx={{
              alignItems: 'center',
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              justifyContent: 'center',
              minHeight: 240,
              width: '100%'
            }}
          >
            <JourneyLoadingScreen appId="quote-personalization-loading" message="Carregando a sua cotação" />
          </Box>
        </StandardJourneyBrandedScreen>
      );
    }
    return (
      <Container maxWidth="lg" sx={styles.container}>
        <Box
          aria-busy="true"
          aria-live="polite"
          role="status"
          sx={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            justifyContent: 'center',
            minHeight: '50vh'
          }}
        >
          <JourneyLoadingScreen appId="quote-personalization-loading-legacy" message="Carregando a sua cotação" />
        </Box>
      </Container>
    );
  }

  if (isJourney) {
    return (
      <StandardJourneyBrandedScreen
        appId="journey-quote-personalization"
        checkoutActiveStep={0}
        contentLayout="checkout"
        onBack={() => navigate(homePath)}
        progressPercent={100 / 3}
        showBack
        showHeaderProgressBar={false}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <StandardJourneyCardHeader
            appId="journey-quote-attribution"
            // Aqui já existe cotação: `plan_club_name` é mais fiel que o clube da rota `flow`.
            clubName={plan_club_name || attributionClubName}
            sellerName={sellerName}
          />
          <JourneyQuoteInsuredHeader
            document={insured.document}
            equipmentLabel={equipment ? searchNameEquipment(equipment) : null}
            equipmentValue={equipmentValue}
            name={displayInsuredName}
          />
          <Grid container spacing={4}>
            <Grid item lg={8} md={7} xs={12}>
              {mainColumnInner}
            </Grid>
            <Grid item lg={4} md={5} xs={12}>
              <JourneyQuoteSummaryColumn
                advanceLabel={isOnlyProduct9 ? 'Ativar garantia' : 'Avançar para pagamento'}
                advancing={contractLoading}
                disableAdvance={shouldDisableSubmit}
                membershipFee={membershipFee}
                productsLoading={productsLoading}
                serviceChargeFormatted={priceFormatter(serviceCharge)}
                showOperationalRow={!isOnlyProduct9}
                subscriptionRows={journeySubscriptionRows}
                totalMonthlyNumber={journeyMonthlyTotal}
                onAdvance={handleContract}
                onDownloadPdf={handleDownloadPdf}
                termsSlot={termsBlock}
              />
            </Grid>
          </Grid>
        </Box>
        <QuotePdfDocument model={quotePdfModel} />
      </StandardJourneyBrandedScreen>
    );
  }

  return (
    <Container maxWidth="lg" sx={styles.container}>
      <Grid container spacing={2}>
        <Grid item md={8} xs={12}>
          <Card>
            <CardContent>{mainColumnInner}</CardContent>
          </Card>
        </Grid>
        <Grid
          item
          md={4}
          sx={{
            display: { lg: 'block', xs: 'none' }
          }}
        >
          <Card>
            <CardContent>
              <SidebarMesages loading={productsLoading} />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
}
